<div class="aca-admin aca-upload" (dragover)="onDragOver($event)" (dragleave)="onDragLeave($event)" (drop)="onDrop($event)">
  <aca-page-header icon="cloud_upload" [title]="'DMS.UPLOAD.PAGE_TITLE' | translate" [subtitle]="'DMS.UPLOAD.PAGE_SUB' | translate" />

  <div class="aca-upload__notice" *ngIf="schemaMissing()">
    <mat-icon>error_outline</mat-icon>
    <div>
      <strong>{{ 'DMS.DOCUMENT_UPLOAD.982276' | translate }}</strong>
      <p>
        {{ 'DMS.DOCUMENT_UPLOAD.6EE7B4' | translate }}<code>dms:documentInfo</code>{{ 'DMS.DOCUMENT_UPLOAD.35C0A3' | translate
        }}<code>node server-extension/content-model/deploy-model.mjs</code>
      </p>
    </div>
  </div>

  <!-- update mode: the reader is versioning this document, not creating one -->
  <section class="aca-upload__update" *ngIf="updateTarget() as target">
    <mat-icon class="aca-upload__updateicon" aria-hidden="true">edit_document</mat-icon>
    <div class="aca-upload__updatebody">
      <strong>{{ 'DMS.UPLOAD.UPDATE_TITLE' | translate }}</strong>
      <p class="aca-upload__updaterow">
        {{ 'DMS.UPLOAD.UPDATE_DOC' | translate }}: {{ target.name }}
        <span class="aca-upload__updateid">ID: {{ target.id }}</span>
      </p>
      <p class="aca-upload__updaterow">
        {{ 'DMS.UPLOAD.UPDATE_CURRENT_VER' | translate }}: {{ currentVersion() || '—' }}
        <span *ngIf="nextVersion()">→ {{ 'DMS.UPLOAD.UPDATE_NEW_VER' | translate }}: {{ nextVersion() }}</span>
      </p>
    </div>
    <button type="button" class="aca-upload__ghostbtn" (click)="exitUpdate()">{{ 'DMS.UPLOAD.UPDATE_EXIT' | translate }}</button>
  </section>

  <!-- step indicator -->
  <ol class="aca-upload__steps" aria-label="steps">
    <li class="aca-upload__step" *ngFor="let s of steps" [ngClass]="stepClass(s.n)">
      <span class="aca-upload__stepnum">
        <mat-icon *ngIf="s.n < step()">check</mat-icon>
        <ng-container *ngIf="s.n >= step()">{{ s.n }}</ng-container>
      </span>
      <span class="aca-upload__steplabel">{{ s.key | translate }}</span>
    </li>
  </ol>

  <div class="aca-upload__draghint" *ngIf="dragging()"><mat-icon>cloud_upload</mat-icon>{{ 'DMS.DOCUMENT_UPLOAD.73824F' | translate }}</div>

  <!-- ── step 1 · pick files ─────────────────────────────────────────────── -->
  <ng-container *ngIf="step() === 1">
    <section class="aca-upload__card">
      <div class="aca-upload__dropzone aca-upload__dropzone--hero" [class.aca-upload__dropzone--active]="dragging()">
        <input
          #fileInput
          type="file"
          class="aca-upload__fileinput"
          multiple
          [attr.accept]="accept"
          (change)="onFileInput($event)"
          [attr.aria-label]="'DMS.DOCUMENT_UPLOAD.5724BC' | translate"
        />
        <mat-icon class="aca-upload__dropicon" aria-hidden="true">cloud_upload</mat-icon>
        <p class="aca-upload__droptitle">{{ 'DMS.DOCUMENT_UPLOAD.73824F' | translate }}</p>
        <p class="aca-upload__drophint">{{ 'DMS.DOCUMENT_UPLOAD.C5F0B5' | translate }}</p>
        <p class="aca-upload__dropformats">{{ 'DMS.DOCUMENT_UPLOAD.7BF8D8' | translate }}</p>
        <button class="aca-upload__filebtn" type="button" (click)="triggerFile(fileInput)">
          <mat-icon>upload_file</mat-icon>{{ 'DMS.DOCUMENT_UPLOAD.094C59' | translate }}
        </button>
      </div>
    </section>
  </ng-container>

  <!-- ── steps 2–3 · review + metadata, one workspace ───────────────────── -->
  <div class="aca-upload__workspace" *ngIf="step() === 2 || step() === 3">
    <!-- left: what the file actually looks like, before it is committed -->
    <section class="aca-upload__card aca-upload__card--preview">
      <app-local-file-preview [file]="selected()?.file ?? null" (pagesKnown)="onPagesKnown($event)" />
    </section>

    <!-- right: metadata + extra options -->
    <div class="aca-upload__col">
      <section class="aca-upload__card">
        <div class="aca-upload__cardhead">
          <h2 class="aca-upload__cardtitle">{{ 'DMS.DOCUMENT_UPLOAD.C71AD9' | translate }}</h2>
          <div class="aca-upload__tabs" role="tablist">
            <button
              type="button"
              role="tab"
              class="aca-upload__tab"
              [class.aca-upload__tab--active]="metaTab() === 'main'"
              [attr.aria-selected]="metaTab() === 'main'"
              (click)="metaTab.set('main')"
            >
              {{ 'DMS.UPLOAD.META_MAIN' | translate }}
            </button>
            <button
              type="button"
              role="tab"
              class="aca-upload__tab"
              [class.aca-upload__tab--active]="metaTab() === 'extra'"
              [attr.aria-selected]="metaTab() === 'extra'"
              (click)="metaTab.set('extra')"
            >
              {{ 'DMS.UPLOAD.META_EXTRA' | translate }}
            </button>
          </div>
        </div>
        <p class="aca-upload__cardsub">{{ 'DMS.DOCUMENT_UPLOAD.79CF82' | translate }}</p>

        <!-- the AI reads the file in the preview and fills only what is still empty -->
        <div class="aca-upload__aibar" *ngIf="canSuggest() && suggestSource() as source">
          <button type="button" class="aca-upload__outlinebtn" [disabled]="suggesting() || busy()" (click)="suggest()">
            <mat-icon>auto_awesome</mat-icon>{{ (suggesting() ? 'DMS.ASSISTANT.CLASSIFY_BUSY' : 'DMS.ASSISTANT.CLASSIFY_BUTTON') | translate }}
          </button>
          <span class="aca-upload__aihint">{{ 'DMS.ASSISTANT.CLASSIFY_UPLOAD_HINT' | translate: { name: source.name } }}</span>
        </div>

        <div class="aca-upload__fields" *ngIf="metaTab() === 'main'" (input)="metaDirty.set(true)">
          <div class="aca-upload__field">
            <label for="dms-title">
              {{ 'DMS.DOCUMENT_UPLOAD.85B482' | translate }}<span class="aca-upload__req">*</span>
              <span class="aca-upload__ai" *ngIf="isSuggested('cm:title')" [matTooltip]="'DMS.ASSISTANT.CLASSIFY_BADGE_HINT' | translate">
                {{ 'DMS.ASSISTANT.CLASSIFY_BADGE' | translate }}
              </span>
            </label>
            <input
              id="dms-title"
              type="text"
              class="aca-upload__input"
              [class.aca-upload__input--invalid]="fieldMissing('title')"
              [(ngModel)]="form.title"
              name="title"
            />
            <span class="aca-upload__fielderror" *ngIf="fieldMissing('title')">{{ 'DMS.UPLOAD.REQUIRED' | translate }}</span>
          </div>

          <div class="aca-upload__field">
            <label for="dms-code">
              {{ 'DMS.DOCUMENT_UPLOAD.0B315E' | translate }}
              <span class="aca-upload__ai" *ngIf="isSuggested('dms:documentCode')" [matTooltip]="'DMS.ASSISTANT.CLASSIFY_BADGE_HINT' | translate">
                {{ 'DMS.ASSISTANT.CLASSIFY_BADGE' | translate }}
              </span>
            </label>
            <input id="dms-code" type="text" class="aca-upload__input" [(ngModel)]="form.documentCode" name="documentCode" />
          </div>

          <div class="aca-upload__field">
            <label for="dms-type">
              {{ 'DMS.DOCUMENT_UPLOAD.EA3757' | translate }}<span class="aca-upload__req">*</span>
              <span class="aca-upload__ai" *ngIf="isSuggested('dms:documentType')" [matTooltip]="'DMS.ASSISTANT.CLASSIFY_BADGE_HINT' | translate">
                {{ 'DMS.ASSISTANT.CLASSIFY_BADGE' | translate }}
              </span>
            </label>
            <select
              id="dms-type"
              class="aca-upload__input aca-upload__select"
              [class.aca-upload__input--invalid]="fieldMissing('documentType')"
              [(ngModel)]="form.documentType"
              name="documentType"
            >
              <option value="">{{ 'DMS.DOCUMENT_UPLOAD.14CFB0' | translate }}</option>
              <option *ngFor="let option of documentTypes" [value]="option.value">{{ option.label | translate }}</option>
            </select>
            <span class="aca-upload__fielderror" *ngIf="fieldMissing('documentType')">{{ 'DMS.UPLOAD.REQUIRED' | translate }}</span>
          </div>

          <div class="aca-upload__field">
            <label for="dms-date">
              {{ 'DMS.DOCUMENT_UPLOAD.58A77B' | translate }}<span class="aca-upload__req">*</span>
              <span class="aca-upload__ai" *ngIf="isSuggested('dms:documentDate')" [matTooltip]="'DMS.ASSISTANT.CLASSIFY_BADGE_HINT' | translate">
                {{ 'DMS.ASSISTANT.CLASSIFY_BADGE' | translate }}
              </span>
            </label>
            <aca-date-input
              inputId="dms-date"
              class="aca-upload__input"
              [class.aca-upload__input--invalid]="fieldMissing('documentDate')"
              [(ngModel)]="form.documentDate"
              name="documentDate"
            />
            <span class="aca-upload__fielderror" *ngIf="fieldMissing('documentDate')">{{ 'DMS.UPLOAD.REQUIRED' | translate }}</span>
          </div>

          <div class="aca-upload__field">
            <label for="dms-department">
              {{ 'DMS.DOCUMENT_UPLOAD.74B287' | translate }}
              <span class="aca-upload__ai" *ngIf="isSuggested('dms:department')" [matTooltip]="'DMS.ASSISTANT.CLASSIFY_BADGE_HINT' | translate">
                {{ 'DMS.ASSISTANT.CLASSIFY_BADGE' | translate }}
              </span>
            </label>
            <input id="dms-department" type="text" class="aca-upload__input" [(ngModel)]="form.department" name="department" />
          </div>

          <div class="aca-upload__field">
            <label for="dms-owner">
              {{ 'DMS.DOCUMENT_UPLOAD.B4F2EC' | translate }}
              <span class="aca-upload__ai" *ngIf="isSuggested('dms:documentOwner')" [matTooltip]="'DMS.ASSISTANT.CLASSIFY_BADGE_HINT' | translate">
                {{ 'DMS.ASSISTANT.CLASSIFY_BADGE' | translate }}
              </span>
            </label>
            <input
              id="dms-owner"
              type="text"
              class="aca-upload__input"
              [placeholder]="'DMS.DOCUMENT_UPLOAD.07A54D' | translate"
              [(ngModel)]="form.documentOwner"
              name="documentOwner"
            />
          </div>

          <div class="aca-upload__field">
            <label for="dms-status">{{ 'DMS.DOCUMENT_UPLOAD.46FAB5' | translate }}<span class="aca-upload__req">*</span></label>
            <select
              id="dms-status"
              class="aca-upload__input aca-upload__select"
              [class.aca-upload__input--invalid]="fieldMissing('status')"
              [(ngModel)]="form.status"
              name="status"
            >
              <option *ngFor="let option of statuses" [value]="option.value">{{ option.label | translate }}</option>
            </select>
            <span class="aca-upload__fielderror" *ngIf="fieldMissing('status')">{{ 'DMS.UPLOAD.REQUIRED' | translate }}</span>
          </div>

          <div class="aca-upload__field">
            <label for="dms-confidentiality">{{ 'DMS.DOCUMENT_UPLOAD.5DDB25' | translate }}</label>
            <select id="dms-confidentiality" class="aca-upload__input aca-upload__select" [(ngModel)]="form.confidentiality" name="confidentiality">
              <option *ngFor="let option of confidentialities" [value]="option.value">{{ option.label | translate }}</option>
            </select>
          </div>

          <div class="aca-upload__field">
            <label for="dms-expiry">
              {{ 'DMS.DOCUMENT_UPLOAD.F51C27' | translate }}
              <span class="aca-upload__ai" *ngIf="isSuggested('cm:to')" [matTooltip]="'DMS.ASSISTANT.CLASSIFY_BADGE_HINT' | translate">
                {{ 'DMS.ASSISTANT.CLASSIFY_BADGE' | translate }}
              </span>
            </label>
            <aca-date-input inputId="dms-expiry" class="aca-upload__input" [(ngModel)]="form.expiryDate" name="expiryDate" />
          </div>

          <div class="aca-upload__field aca-upload__field--full">
            <label for="dms-description">
              {{ 'DMS.DOCUMENT_UPLOAD.9FB99F' | translate }}
              <span class="aca-upload__ai" *ngIf="isSuggested('cm:description')" [matTooltip]="'DMS.ASSISTANT.CLASSIFY_BADGE_HINT' | translate">
                {{ 'DMS.ASSISTANT.CLASSIFY_BADGE' | translate }}
              </span>
            </label>
            <textarea
              id="dms-description"
              rows="4"
              class="aca-upload__input aca-upload__textarea"
              [maxlength]="maxDescription"
              [(ngModel)]="form.description"
              name="description"
            ></textarea>
            <span class="aca-upload__counter">{{ form.description.length }}/{{ maxDescription }}</span>
          </div>
        </div>

        <!-- fields the model gained from the settings screen, rendered generically -->
        <div class="aca-upload__fields" *ngIf="metaTab() === 'extra'" (input)="metaDirty.set(true)">
          <p class="aca-upload__hint aca-upload__field--full" *ngIf="!extraFields().length">{{ 'DMS.UPLOAD.META_EMPTY' | translate }}</p>
          <div class="aca-upload__field" *ngFor="let field of extraFields()">
            <label [attr.for]="'dms-extra-' + field.id">
              {{ fieldLabel(field) | translate }}
              <span class="aca-upload__ai" *ngIf="isSuggested(field.id)" [matTooltip]="'DMS.ASSISTANT.CLASSIFY_BADGE_HINT' | translate">
                {{ 'DMS.ASSISTANT.CLASSIFY_BADGE' | translate }}
              </span>
            </label>

            <ng-container [ngSwitch]="inputKind(field)">
              <select
                *ngSwitchCase="'list'"
                [id]="'dms-extra-' + field.id"
                class="aca-upload__input aca-upload__select"
                [(ngModel)]="extra[field.id]"
                [name]="field.id"
              >
                <option value="">—</option>
                <option *ngFor="let option of field.options" [value]="option.value">{{ option.label | translate }}</option>
              </select>

              <select
                *ngSwitchCase="'boolean'"
                [id]="'dms-extra-' + field.id"
                class="aca-upload__input aca-upload__select"
                [(ngModel)]="extra[field.id]"
                [name]="field.id"
              >
                <option value="">—</option>
                <option value="true">{{ 'DMS.UI.2726B4' | translate }}</option>
                <option value="false">{{ 'DMS.UI.19CE3F' | translate }}</option>
              </select>

              <aca-date-input
                *ngSwitchCase="'date'"
                [inputId]="'dms-extra-' + field.id"
                class="aca-upload__input"
                [(ngModel)]="extra[field.id]"
                [name]="field.id"
              />

              <input
                *ngSwitchCase="'number'"
                [id]="'dms-extra-' + field.id"
                type="number"
                class="aca-upload__input"
                [(ngModel)]="extra[field.id]"
                [name]="field.id"
              />

              <input
                *ngSwitchDefault
                [id]="'dms-extra-' + field.id"
                type="text"
                class="aca-upload__input"
                [(ngModel)]="extra[field.id]"
                [name]="field.id"
              />
            </ng-container>
          </div>
        </div>
      </section>

      <!-- existing options, regrouped: OCR, destination, tags -->
      <section class="aca-upload__card aca-upload__card--options">
        <button type="button" class="aca-upload__opthead" (click)="optionsOpen.set(!optionsOpen())" [attr.aria-expanded]="optionsOpen()">
          <h2 class="aca-upload__cardtitle">{{ 'DMS.UPLOAD.OPTIONS' | translate }}</h2>
          <mat-icon>{{ optionsOpen() ? 'expand_less' : 'expand_more' }}</mat-icon>
        </button>

        <div class="aca-upload__optbody" *ngIf="optionsOpen()">
          <div class="aca-upload__optrow aca-upload__card--ocr">
            <mat-icon class="aca-upload__ocricon" aria-hidden="true">document_scanner</mat-icon>
            <div class="aca-upload__ocrbody">
              <label class="aca-upload__ocrswitch">
                <input type="checkbox" [(ngModel)]="readText" name="readText" [disabled]="busy()" />
                <strong>{{ 'DMS.DOCUMENT_UPLOAD.OCR_TITLE' | translate }}</strong>
              </label>
              <p class="aca-upload__ocrhint">{{ 'DMS.DOCUMENT_UPLOAD.OCR_HINT' | translate }}</p>
              <p class="aca-upload__ocrstate" [ngClass]="'aca-upload__ocrstate--' + engine()" role="status">
                <span class="aca-upload__ocrdot" aria-hidden="true"></span>{{ engineKey() | translate }}
                <button *ngIf="engine() === 'unavailable'" type="button" class="aca-upload__linkbtn" (click)="checkEngine()">
                  {{ 'DMS.DOCUMENT_UPLOAD.OCR_RETRY' | translate }}
                </button>
              </p>
            </div>
          </div>

          <div class="aca-upload__optrow">
            <mat-icon aria-hidden="true">folder</mat-icon>
            <div class="aca-upload__optbody-text">
              <span class="aca-upload__optlabel">{{ 'DMS.DOCUMENT_UPLOAD.7C0D13' | translate }}</span>
              <span class="aca-upload__pathlabel">{{ destinationLabel | translate }}</span>
            </div>
            <button class="aca-upload__outlinebtn" type="button" *ngIf="!updateMode()" (click)="pickDestination()">
              {{ 'DMS.DOCUMENT_UPLOAD.822FF8' | translate }}
            </button>
          </div>

          <div class="aca-upload__optrow aca-upload__optrow--top">
            <mat-icon aria-hidden="true">sell</mat-icon>
            <div class="aca-upload__optbody-text">
              <span class="aca-upload__optlabel">{{ 'DMS.DOCUMENT_UPLOAD.F07115' | translate }}</span>
              <form (submit)="addTag($event)">
                <input
                  type="text"
                  class="aca-upload__input"
                  [placeholder]="'DMS.DOCUMENT_UPLOAD.231274' | translate"
                  [attr.aria-label]="'DMS.DOCUMENT_UPLOAD.231274' | translate"
                  [(ngModel)]="tagDraft"
                  name="tagDraft"
                  autocomplete="off"
                />
              </form>
              <p class="aca-upload__hint">{{ 'DMS.DOCUMENT_UPLOAD.C40360' | translate }}</p>
              <div class="aca-upload__chips" *ngIf="tags().length">
                <span class="aca-upload__chip" *ngFor="let tag of tags()">
                  {{ tag }}
                  <button type="button" class="aca-upload__chipx" (click)="removeTag(tag)" [attr.aria-label]="('DMS.UI.499C6E' | translate) + tag">
                    <mat-icon>close</mat-icon>
                  </button>
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>

  <!-- file queue: full width, below the workspace -->
  <section class="aca-upload__card aca-upload__queue" *ngIf="queue().length && step() !== 4">
    <div class="aca-upload__cardhead">
      <h2 class="aca-upload__cardtitle">{{ 'DMS.UPLOAD.QUEUE_TITLE' | translate: { count: queue().length } }}</h2>
      <div class="aca-upload__queueactions">
        <button class="aca-upload__outlinebtn" type="button" (click)="moreInput.click()" [disabled]="busy()">
          <mat-icon>add</mat-icon>{{ 'DMS.UPLOAD.ADD_MORE' | translate }}
        </button>
        <input #moreInput type="file" class="aca-upload__fileinput" multiple [attr.accept]="accept" (change)="onFileInput($event)" />
        <button class="aca-upload__ghostbtn aca-upload__ghostbtn--danger" type="button" (click)="clearQueue()" [disabled]="busy()">
          <mat-icon>delete_sweep</mat-icon>{{ 'DMS.UPLOAD.CLEAR_ALL' | translate }}
        </button>
      </div>
    </div>

    <div class="aca-upload__tablewrap">
      <table class="aca-upload__table">
        <thead>
          <tr>
            <th class="aca-upload__th--num">#</th>
            <th>{{ 'DMS.UPLOAD.COL_NAME' | translate }}</th>
            <th>{{ 'DMS.UPLOAD.COL_TYPE' | translate }}</th>
            <th>{{ 'DMS.UPLOAD.COL_SIZE' | translate }}</th>
            <th>{{ 'DMS.UPLOAD.COL_PAGES' | translate }}</th>
            <th>{{ 'DMS.UPLOAD.COL_THUMB' | translate }}</th>
            <th>{{ 'DMS.UPLOAD.COL_STATUS' | translate }}</th>
            <th class="aca-upload__th--act">{{ 'DMS.UPLOAD.COL_ACTIONS' | translate }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            *ngFor="let item of queue(); let i = index"
            [class.aca-upload__row--selected]="selectedIndex() === i"
            [class.aca-upload__row--failed]="item.state === 'failed'"
            (click)="viewFile(i)"
          >
            <td class="aca-upload__td--num">{{ i + 1 }}</td>
            <td class="aca-upload__td--name">
              <span class="aca-upload__filename">{{ item.file.name }}</span>
              <span class="aca-upload__fileerror" *ngIf="item.error">{{ item.error }}</span>
              <span class="aca-upload__ocr" *ngIf="ocrLine(item) as line" [ngClass]="'aca-upload__ocr--' + line.tone">
                <mat-icon aria-hidden="true">{{ line.icon }}</mat-icon
                >{{ line.key | translate: line.params }}
              </span>
            </td>
            <td>{{ typeLabel(item.file.name) }}</td>
            <td>{{ formatSize(item.file.size) }}</td>
            <td>{{ item.pages ?? '—' }}</td>
            <td>
              <img *ngIf="item.previewUrl" class="aca-upload__thumbcell" [src]="item.previewUrl" [alt]="item.file.name" />
              <span *ngIf="!item.previewUrl" class="aca-upload__badge" [ngClass]="badgeClass(item.file.name)">{{ badge(item.file.name) }}</span>
            </td>
            <td>
              <span class="aca-upload__status" [ngClass]="'aca-upload__status--' + statusOf(item)">
                {{ statusKey(item) | translate }}
              </span>
              <mat-progress-bar class="aca-upload__filebar" mode="indeterminate" *ngIf="item.state === 'uploading' || item.ocrState === 'reading'" />
            </td>
            <td class="aca-upload__td--act" (click)="$event.stopPropagation()">
              <button
                mat-icon-button
                type="button"
                [matTooltip]="'DMS.UPLOAD.ACT_VIEW' | translate"
                [attr.aria-label]="'DMS.UPLOAD.ACT_VIEW' | translate"
                (click)="viewFile(i)"
              >
                <mat-icon>visibility</mat-icon>
              </button>
              <button
                mat-icon-button
                type="button"
                [matTooltip]="'DMS.UPLOAD.ACT_EDIT' | translate"
                [attr.aria-label]="'DMS.UPLOAD.ACT_EDIT' | translate"
                (click)="editFile(i)"
                [disabled]="busy()"
              >
                <mat-icon>edit</mat-icon>
              </button>
              <button
                mat-icon-button
                type="button"
                class="aca-upload__del"
                [class.aca-upload__del--armed]="confirmDelete() === i"
                [matTooltip]="confirmDelete() === i ? ('DMS.UPLOAD.CONFIRM_REMOVE' | translate) : ('DMS.UPLOAD.ACT_REMOVE' | translate)"
                [attr.aria-label]="confirmDelete() === i ? ('DMS.UPLOAD.CONFIRM_REMOVE' | translate) : ('DMS.UPLOAD.ACT_REMOVE' | translate)"
                *ngIf="item.state === 'ready' || item.state === 'failed'"
                (click)="removeFile(i)"
                [disabled]="busy()"
              >
                <mat-icon>{{ confirmDelete() === i ? 'delete_forever' : 'delete' }}</mat-icon>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>

  <!-- what the last upload filed: the queue is cleared, so this is where it shows -->
  <section class="aca-upload__card aca-upload__card--done" *ngIf="lastBatch().length && step() !== 4">
    <div class="aca-upload__cardhead">
      <h2 class="aca-upload__cardtitle aca-upload__donetitle">
        <mat-icon aria-hidden="true">task_alt</mat-icon>{{ 'DMS.DOCUMENT_UPLOAD.BATCH_TITLE' | translate: { count: lastBatch().length } }}
      </h2>
      <button class="aca-upload__ghostbtn" type="button" (click)="dismissBatch()">{{ 'DMS.DOCUMENT_UPLOAD.BATCH_MORE' | translate }}</button>
    </div>
    <p class="aca-upload__hint aca-upload__donewhere">
      <mat-icon aria-hidden="true">folder</mat-icon>{{ 'DMS.DOCUMENT_UPLOAD.BATCH_WHERE' | translate }} {{ destinationLabel | translate }}
    </p>

    <ul class="aca-upload__files">
      <li class="aca-upload__file" *ngFor="let item of lastBatch()">
        <span class="aca-upload__badge" [ngClass]="badgeClass(item.file.name)">{{ badge(item.file.name) }}</span>
        <span class="aca-upload__filemeta">
          <span class="aca-upload__filename">{{ item.file.name }}</span>
          <span class="aca-upload__fileerror" *ngIf="item.error">{{ item.error }}</span>
          <span class="aca-upload__ocr" *ngIf="ocrLine(item) as line" [ngClass]="'aca-upload__ocr--' + line.tone">
            <mat-icon aria-hidden="true">{{ line.icon }}</mat-icon
            >{{ line.key | translate: line.params }}
          </span>
        </span>
        <button class="aca-upload__ghostbtn" type="button" (click)="openDocument(item.nodeId)" *ngIf="item.nodeId">
          {{ 'DMS.DOCUMENT_UPLOAD.BATCH_OPEN' | translate }}
        </button>
      </li>
    </ul>
  </section>

  <!-- ── step 4 · confirm and save ────────────────────────────────────────── -->
  <section class="aca-upload__card aca-upload__confirm" *ngIf="step() === 4">
    <h2 class="aca-upload__cardtitle">{{ 'DMS.UPLOAD.STEP_CONFIRM' | translate }}</h2>

    <dl class="aca-upload__summary" *ngIf="!updateMode()">
      <div>
        <dt>{{ 'DMS.UPLOAD.SUM_FILES' | translate }}</dt>
        <dd>{{ queue().length }}</dd>
      </div>
      <div>
        <dt>{{ 'DMS.UPLOAD.SUM_READY' | translate }}</dt>
        <dd>{{ readyCount() }}</dd>
      </div>
      <div>
        <dt>{{ 'DMS.UPLOAD.SUM_DEST' | translate }}</dt>
        <dd>{{ destinationLabel | translate }}</dd>
      </div>
      <div>
        <dt>{{ 'DMS.UPLOAD.SUM_OCR' | translate }}</dt>
        <dd>{{ (readText ? 'DMS.UPLOAD.SUM_OCR_ON' : 'DMS.UPLOAD.SUM_OCR_OFF') | translate }}</dd>
      </div>
      <div *ngIf="tags().length">
        <dt>{{ 'DMS.DOCUMENT_UPLOAD.F07115' | translate }}</dt>
        <dd>{{ tags().join(', ') }}</dd>
      </div>
    </dl>

    <dl class="aca-upload__summary" *ngIf="updateMode()">
      <div>
        <dt>{{ 'DMS.UPLOAD.UPDATE_DOC' | translate }}</dt>
        <dd>{{ updateTarget()?.name }}</dd>
      </div>
      <div>
        <dt>{{ 'DMS.UPLOAD.UPDATE_CURRENT_VER' | translate }}</dt>
        <dd>{{ currentVersion() || '—' }}</dd>
      </div>
      <div>
        <dt>{{ 'DMS.UPLOAD.UPDATE_NEW_VER' | translate }}</dt>
        <dd>{{ nextVersion() || '—' }} ({{ (updateMajor ? 'DMS.UPLOAD.VER_MAJOR' : 'DMS.UPLOAD.VER_MINOR') | translate }})</dd>
      </div>
      <div>
        <dt>{{ 'DMS.UPLOAD.SUM_NEW_FILE' | translate }}</dt>
        <dd>{{ queue()[0]?.file?.name }}</dd>
      </div>
      <div>
        <dt>{{ 'DMS.UPLOAD.SUM_OCR' | translate }}</dt>
        <dd>{{ (readText ? 'DMS.UPLOAD.SUM_OCR_ON' : 'DMS.UPLOAD.SUM_OCR_OFF') | translate }}</dd>
      </div>
    </dl>

    <div class="aca-upload__veropts" *ngIf="updateMode()">
      <label class="aca-upload__ocrswitch">
        <input type="checkbox" [(ngModel)]="updateMajor" name="updateMajor" />
        <strong>{{ 'DMS.UPLOAD.VER_MAJOR' | translate }}</strong>
      </label>
      <div class="aca-upload__field aca-upload__field--full">
        <label for="ver-comment">{{ 'DMS.UPLOAD.VER_COMMENT' | translate }}</label>
        <input id="ver-comment" type="text" class="aca-upload__input" [(ngModel)]="versionComment" name="versionComment" />
      </div>
    </div>

    <!-- per-file progress while filing, one row each -->
    <ul class="aca-upload__progress" *ngIf="busy()">
      <li *ngFor="let item of queue()">
        <span class="aca-upload__filename">{{ item.file.name }}</span>
        <mat-progress-bar mode="indeterminate" *ngIf="item.state === 'uploading' || item.ocrState === 'reading'" />
        <span class="aca-upload__status" [ngClass]="'aca-upload__status--' + statusOf(item)">{{ statusKey(item) | translate }}</span>
      </li>
    </ul>
  </section>

  <footer class="aca-upload__footer">
    <button class="aca-upload__btn aca-upload__btn--plain" type="button" (click)="cancel()" [disabled]="busy()" *ngIf="step() < 4">
      {{ 'DMS.DOCUMENT_UPLOAD.FBA6EF' | translate }}
    </button>

    <button class="aca-upload__btn aca-upload__btn--outline" type="button" (click)="back()" *ngIf="step() > 1" [disabled]="busy()">
      <mat-icon>arrow_back</mat-icon>{{ 'DMS.UPLOAD.BACK' | translate }}
    </button>

    <ng-container *ngIf="step() < 4">
      <button class="aca-upload__btn aca-upload__btn--primary" type="button" (click)="next()" [disabled]="step() === 1 && !queue().length">
        {{ 'DMS.UPLOAD.NEXT' | translate }}<mat-icon>arrow_forward</mat-icon>
      </button>
    </ng-container>

    <ng-container *ngIf="step() === 4">
      <button class="aca-upload__btn aca-upload__btn--outline" type="button" (click)="saveDraft()" [disabled]="!canSubmit()">
        <mat-icon>save</mat-icon>{{ 'DMS.DOCUMENT_UPLOAD.BB18E6' | translate }}
      </button>
      <button class="aca-upload__btn aca-upload__btn--primary" type="button" (click)="submit()" [disabled]="!canSubmit()">
        <mat-icon>cloud_upload</mat-icon>
        {{ busy() ? ('DMS.UI.837FB2' | translate) : ((updateMode() ? 'DMS.UPLOAD.CONFIRM_UPDATE' : 'DMS.UPLOAD.CONFIRM') | translate) }}
      </button>
    </ng-container>
  </footer>
</div>
